import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useManageAddress } from "@/api/AppApi/services/address";
import { ApiMsgTypes } from "@/api/AppApi/common-interfaces/IResultStamp";
import { useGetAddressQuery } from "@/api/AppApi/services/builder";
import { IField } from "@/api/AppApi/services/builder/interfaces/interfaces";
import AddressManageBtns from "./addresses-btns/AddressManageBtns";
import {
  adaptFieldsToPost,
  defaultValues,
  formErrors,
  renameFields,
} from "@/app/components/common-form-builder/form-builder-helpers/form-functions";
import CommonFormBuilder from "@/app/components/common-form-builder/CommonFormBuilder";
import GeneralApiLoader from "@/app/components/lib/loaders/GeneralApiLoader";
import BaseOption from "@/app/components/common-filter-builder/common-filters-components/base-checkbox/BaseOption";
import NotFieldErrModal from "@/app/components/common-form-builder/form-builder-helpers/NotFieldErrModal";
import { useTranslation } from "@/hooks/localization/useTranslations";

interface Props {
  id?: number | null;
  onManageSuccess: () => void;
  onCancel: () => void;
}

const ManageAddress: React.FC<Props> = ({ id, onManageSuccess, onCancel }) => {
  const t = useTranslation();
  const [isDefault, setIsDefault] = useState(false);
  const getAddress = useGetAddressQuery(id ?? undefined);
  const builder = getAddress?.data?.result?.builder;
  const [addressFields, setAddressFields] = useState<IField[]>();
  const [notFieldErr, setNotFieldErr] = useState<string[]>([]);
  const reactHookForm = useForm();
  const manageAddress = useManageAddress({
    onSuccess: (res) => {
      if (res?.message?.type === ApiMsgTypes.success) {
        getAddress.refetch();
        onManageSuccess();
      }
    },
    onError: (error) => {
      const extraErr = formErrors(error, reactHookForm);
      setNotFieldErr(extraErr);
    },
  });

  const onSubmit = (data: any) => {
    const adaptedFields = adaptFieldsToPost(data);
    manageAddress.mutate({ ...adaptedFields, is_default: isDefault, id: id });
  };

  const handleInnerSubmit = (event: React.BaseSyntheticEvent) => {
    event.stopPropagation();
    reactHookForm.handleSubmit(onSubmit)(event);
  };
  useEffect(() => {
    if (getAddress.isFetching) return;
    const fields = renameFields(builder);
    reactHookForm.reset(defaultValues(fields));
    setAddressFields(fields);
    setIsDefault(getAddress?.data?.result?.data?.is_default ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAddress.isLoading, getAddress.isFetching, id]);

  if (getAddress.isFetching) return <GeneralApiLoader />;

  return (
    <>
      {notFieldErr.length > 0 && (
        <NotFieldErrModal errors={notFieldErr} setErrors={setNotFieldErr} />
      )}
      <form
        onSubmit={handleInnerSubmit}
        className="auth-form"
        id="address-form"
      >
        {addressFields?.map((field) => (
          <CommonFormBuilder
            key={field?.configurations?.key}
            field={field}
            form={reactHookForm}
          />
        ))}
        <BaseOption
          selected={isDefault}
          toggleOption={() => setIsDefault(!isDefault)}
          is_multi_select
          label={t.default}
        />
        <AddressManageBtns
          handleCancel={() => onCancel()}
          isLoading={manageAddress.isLoading}
          disabled={!reactHookForm?.formState?.isValid}
        />
      </form>
    </>
  );
};

export default ManageAddress;
